import { z } from 'zod';
import { idSchema } from './ids';

/**
 * Field schemas shared by every entity form and API body (ADR 0010/0011). Forms send strings;
 * an empty field means "not set" (`null`). Written once here — never per entity.
 */

/** Empty string → null; `undefined` stays `undefined` (field not sent). */
const emptyToNull = (v: string) => (v === '' ? null : v);

export const requiredText = (max: number, message = 'Obligāts lauks') =>
  z.string().trim().min(1, { message }).max(max);

export const optionalText = (max: number) =>
  z.string().trim().max(max).transform(emptyToNull).nullable().optional();

export const optionalUrl = z
  .string()
  .trim()
  .transform(emptyToNull)
  .pipe(z.url({ message: 'Nederīga saite' }).nullable())
  .nullable()
  .optional();

export const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .transform(emptyToNull)
  .pipe(z.email({ message: 'Nederīga e-pasta adrese' }).nullable())
  .nullable()
  .optional();

/** Optional choice from a select: the empty option ('') means "not set" (null). */
export const optionalChoice = <T extends z.ZodType<string>>(schema: T) =>
  z
    .union([z.literal(''), schema])
    .transform((v) => (v === '' ? null : (v as z.output<T>)))
    .nullable()
    .optional();

/** Optional reference to another record (select with an empty option). */
export const optionalId = optionalChoice(idSchema);

/** Calendar date without time, `YYYY-MM-DD` (database `date`). */
export const dateSchema = z.iso.date({ message: 'Nederīgs datums' });

export const requiredDate = (message = 'Obligāts lauks') => z.string().min(1, { message }).pipe(dateSchema);

export const optionalDate = z
  .string()
  .transform(emptyToNull)
  .pipe(dateSchema.nullable())
  .nullable()
  .optional();

/** Instant with offset (ISO 8601) — e.g. a note's date and time. */
export const dateTimeSchema = z.iso.datetime({ offset: true, message: 'Nederīgs datums un laiks' });

/** Number typed into a form ("12,5" or "12.5"); empty → null. */
export const optionalNumber = (opts: { min?: number; max?: number; int?: boolean } = {}) => {
  let n = z.number({ message: 'Ievadiet skaitli' });
  if (opts.int) n = n.int({ message: 'Ievadiet veselu skaitli' });
  if (opts.min !== undefined) n = n.min(opts.min, { message: `Ne mazāk par ${opts.min}` });
  if (opts.max !== undefined) n = n.max(opts.max, { message: `Ne vairāk par ${opts.max}` });
  return z
    .union([z.number(), z.string().trim()])
    .transform((v) => (typeof v === 'number' ? v : v === '' ? null : Number(v.replace(',', '.'))))
    .pipe(n.nullable())
    .nullable()
    .optional();
};

/** Hours (plan/fact): 0 … 100 000, step free (UI suggests 0.5). */
export const optionalHours = optionalNumber({ min: 0, max: 100_000 });

/**
 * Money as a decimal string with at most 2 decimals ("1234.5" → "1234.50"); money never travels
 * as a float (ADR 0006). Accepts "1 234,50" from forms.
 */
export const moneyPattern = /^-?\d{1,12}(\.\d{1,2})?$/;
export const optionalMoney = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim().replace(/\s/g, '').replace(',', '.')))
  .transform(emptyToNull)
  .pipe(
    z
      .string()
      .regex(moneyPattern, { message: 'Nederīga summa' })
      .transform((v) => Number(v).toFixed(2))
      .nullable(),
  )
  .nullable()
  .optional();

/** Money as returned by the API: decimal string with 2 decimals, or null. */
export const moneyOutSchema = z
  .string()
  .regex(/^-?\d+\.\d{2}$/)
  .nullable();

/** `?flag=true|false` query parameter (z.coerce.boolean would read "false" as true). */
export const queryBoolean = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true')
  .optional();

/** Comma-separated query list → array (empty → []). */
export const queryList = <T extends z.ZodType<string, string>>(item: T) =>
  z
    .string()
    .transform((v) => (v === '' ? [] : v.split(',').map((s) => s.trim())))
    .pipe(z.array(item))
    .optional();

/** Optimistic concurrency: an update carries the version it was based on (ADR 0006). */
export const versionField = { version: z.number().int().min(0) };
